export type PresignedFields = Record<string, string>;

// Refresh a little before expiry so a link never lapses between fetch and use.
const LINK_REFRESH_MARGIN_MS = 15_000;

export class PresignedPostError extends Error {
  status: number;

  constructor(status: number) {
    super("Upload to storage failed");
    this.name = "PresignedPostError";
    this.status = status;
  }
}

// AWSJSON arrives as an object or as a JSON string depending on the resolver.
export const parsePresignedFields = (fields: PresignedFields | string): PresignedFields =>
  typeof fields === "string" ? JSON.parse(fields) : fields;

interface PostFileOptions {
  url: string;
  fields: PresignedFields;
  file: File;
  onProgress?: (_percent: number) => void;
  signal?: AbortSignal;
}

// Multipart POST to an S3 presigned POST URL; every policy field must precede the file part.
export const postFileToPresignedUrl = ({ url, fields, file, onProgress, signal }: PostFileOptions): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }

    const formData = new FormData();

    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append("file", file);

    const xhr = new XMLHttpRequest();

    xhr.open("POST", url);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new PresignedPostError(xhr.status));
    };
    xhr.onerror = () => reject(new PresignedPostError(0));
    xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(formData);
  });

export const isLinkStale = (expiresAtMs: number): boolean => Date.now() >= expiresAtMs - LINK_REFRESH_MARGIN_MS;

export const openPresignedUrl = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

// For attachment-disposition links: the browser downloads and the page stays put.
export const downloadPresignedUrl = (url: string) => {
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};
